/**
 * VideoStage - Main video display area
 * Full-screen layout similar to Google Meet / Microsoft Teams:
 * - Remote participant fills the entire stage (spotlight)
 * - Local camera always shown as small PIP tile (bottom-left)
 * - Uses direct getUserMedia for local preview (works before/without LiveKit connection)
 * - Waiting state with user icon when no remote participants
 */

import * as React from "react";
import { Participant, Track, ParticipantEvent } from "livekit-client";
import { User } from "lucide-react";
import { cn } from "../../lib/utils";
import { ParticipantTile } from "./ParticipantTile";

export interface VideoStageProps {
  localParticipant: Participant | null;
  remoteParticipants: Participant[];
  spotlightParticipant: Participant | null;
  isCameraEnabled?: boolean;
  isMirrored?: boolean;
  waitingMessage?: string;
  waitingSubtitle?: string;
  localLabel?: string;
  className?: string;
}

export function VideoStage({
  localParticipant,
  remoteParticipants,
  spotlightParticipant,
  isCameraEnabled = true,
  isMirrored = true,
  waitingMessage = "Warte auf andere Teilnehmer...",
  waitingSubtitle,
  localLabel = "Sie",
  className,
}: VideoStageProps) {
  // Non-spotlight remote participants for additional small tiles
  const extraRemoteParticipants = React.useMemo(() => {
    return remoteParticipants.filter(
      (p) => p.sid !== spotlightParticipant?.sid
    );
  }, [remoteParticipants, spotlightParticipant]);

  return (
    <div className={cn("relative flex-1 bg-stone-950", className)}>
      {/* Spotlight: remote participant fills entire area */}
      <div className="absolute inset-0 bg-stone-900">
        {spotlightParticipant ? (
          <ParticipantTile
            participant={spotlightParticipant}
            isSpotlight
            className="absolute inset-0 w-full h-full rounded-none"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
            <div className="text-center p-8">
              <User className="h-16 w-16 text-stone-600 mx-auto mb-4" />
              <p className="text-stone-400">{waitingMessage}</p>
              {waitingSubtitle && (
                <p className="text-stone-500 text-sm mt-2">{waitingSubtitle}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Local camera PIP - always shown at bottom-left, responsive sizing */}
      <div className="absolute bottom-4 left-2 w-[100px] h-[75px] sm:left-4 sm:w-36 sm:h-28 md:w-48 md:h-36 bg-stone-900 rounded-xl overflow-hidden shadow-lg border border-stone-700 z-10">
        <LocalPipTile
          participant={localParticipant}
          isCameraEnabled={isCameraEnabled}
          isMirrored={isMirrored}
        />
        <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/60 to-transparent z-20">
          <span className="text-white text-xs">{localLabel}</span>
        </div>
      </div>

      {/* Extra remote participants (non-spotlight) - small tiles next to local */}
      {extraRemoteParticipants.length > 0 && (
        <div className="absolute bottom-4 left-[116px] sm:left-44 md:left-56 flex gap-2 sm:gap-3 z-10">
          {extraRemoteParticipants.map((participant) => (
            <div
              key={participant.sid}
              className="w-[100px] h-[75px] sm:w-36 sm:h-28 md:w-48 md:h-36 bg-stone-900 rounded-xl overflow-hidden shadow-lg border border-stone-700"
            >
              <ParticipantTile
                participant={participant}
                className="absolute inset-0 w-full h-full rounded-none"
                showName={true}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * LocalPipTile - Shows local camera in PIP.
 * Uses direct getUserMedia as primary source (always works).
 * Switches to LiveKit participant track when available.
 */
function LocalPipTile({
  participant,
  isCameraEnabled,
  isMirrored,
}: {
  participant: Participant | null;
  isCameraEnabled: boolean;
  isMirrored: boolean;
}) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const [hasVideo, setHasVideo] = React.useState(false);
  // Track whether LiveKit participant has a camera track
  const [hasLivekitTrack, setHasLivekitTrack] = React.useState(false);

  // Try to use LiveKit participant track when available
  React.useEffect(() => {
    if (!participant) {
      setHasLivekitTrack(false);
      return;
    }

    const attachLivekitTrack = () => {
      const pub = participant.getTrackPublication(Track.Source.Camera);
      if (pub?.track && videoRef.current) {
        // Stop getUserMedia stream since LiveKit has taken over
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        pub.track.attach(videoRef.current);
        setHasLivekitTrack(true);
        setHasVideo(true);
      }
    };

    attachLivekitTrack();

    const onChange = () => attachLivekitTrack();
    participant.on(ParticipantEvent.LocalTrackPublished, onChange);
    participant.on(ParticipantEvent.LocalTrackUnpublished, () => {
      setHasLivekitTrack(false);
    });
    participant.on(ParticipantEvent.TrackMuted, onChange);
    participant.on(ParticipantEvent.TrackUnmuted, onChange);

    return () => {
      participant.off(ParticipantEvent.LocalTrackPublished, onChange);
      participant.off(ParticipantEvent.LocalTrackUnpublished, onChange);
      participant.off(ParticipantEvent.TrackMuted, onChange);
      participant.off(ParticipantEvent.TrackUnmuted, onChange);
    };
  }, [participant]);

  // Fallback: use direct getUserMedia when no LiveKit track
  React.useEffect(() => {
    if (hasLivekitTrack || !isCameraEnabled) return;

    let cancelled = false;

    async function startPreview() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setHasVideo(true);
      } catch {
        setHasVideo(false);
      }
    }

    startPreview();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [hasLivekitTrack, isCameraEnabled]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cn(
          "absolute inset-0 w-full h-full object-cover",
          isMirrored && "-scale-x-100",
          (!hasVideo || !isCameraEnabled) && "hidden"
        )}
      />
      {(!hasVideo || !isCameraEnabled) && (
        <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
          <User className="h-8 w-8 text-stone-500" />
        </div>
      )}
    </>
  );
}
